import { Helmet } from "react-helmet";
import {
  Box,
  ClickAwayListener,
  Stack,
  MenuItem,
  MenuList,
  Divider,
  InputAdornment,
  TextField,
  Tab,
  Tabs,
  Select,
  Container,
  Grid,
  Typography,
  Button,
  usePagination,
  Grow,
  Popper,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Paper,
  Avatar,
} from "@mui/material";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { makeStyles } from "@mui/styles";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import Search from "@mui/icons-material/Search";
import React, { useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import cssVariables from "../../../../theme/cssVariables";
import { styled } from "@mui/styles";

import { ReactComponent as ChevronExpandDown } from "../../../../assets/icons/chevron-expand-down.svg";
import { ReactComponent as ChevronExpandUp } from "../../../../assets/icons/chevron-expand-up.svg";
import { ReactComponent as DeleteIcon } from "../../../../assets/icons/delete.svg";
import { ReactComponent as FilterStatus } from "../../../../assets/icons/filter-status.svg";
import { ReactComponent as Nodata } from "../../../../assets/icons/nodata.svg";
import { ReactComponent as EditIcon } from "../../../../assets/icons/edit.svg";
import { ReactComponent as StarEmpty } from "../../../../icons/star-empty.svg";
import { ReactComponent as StarFill } from "../../../../icons/star.svg";
import { ReactComponent as PlusIcon } from "../../../../icons/plus.svg";
import defaultBanner from "../../../../assets/images/announcement/default.jpg"

import ConfirmDialog from "../../../../components/ConfirmDialog";

import * as Util from "../../../../services/global.service";
import {
  getAnnouncementList,
  deleteAnnouncement
} from "../../../../actions/manageAnnouncement";

function CustomPagination(props) {
  const { t } = useTranslation()
  const useStyles = makeStyles({
    paginationRoot: {
      color: `${cssVariables.color.primary}`,
      backgroundColor: "white!important",
      minWidth: "30px!important",
      padding: "5px!important",
    },
    paginationSelected: {
      color: "white!important",
      border: "1px solid rgba(93, 161, 150, 0.5)!important",
      backgroundColor: `${cssVariables.color.primary}!important`,
      minWidth: "30px!important",
      padding: "5px!important",
    },
  });
  const classes = useStyles();
  const List = styled("ul")({
    listStyle: "none",
    padding: 0,
    margin: 0,
    display: "flex",
  });
  const { items } = usePagination({
    count: props.count,
    showFirstButton: true,
    showLastButton: true,
  });

  useEffect(() => {
    props.setCurrentPage(items)
  }, [items])

  return (
    <nav>
      <List>
        {items.length > 0 ? (
          items.map(({ page, type, selected, ...item }, index) => {
            let children = null;
            selected = type === "page" && page === props.selected ? props.selected : selected
            if (type === "start-ellipsis" || type === "end-ellipsis") {
              children = "…";
            } else if (type === "page") {
              children = (
                <Button
                  {...item}
                  size="small"
                  type="button"
                  style={{
                    fontWeight: selected ? "bold" : undefined,
                  }}
                  variant="outlined"
                  classes={{
                    root: selected
                      ? classes.paginationSelected
                      : classes.paginationRoot,
                  }}
                  sx={{ margin: "0 5px" }}
                >
                  {page}
                </Button>
              );
            } else if (type === "first" || type === "last") {
              children = (
                <Button
                  {...item}
                  disabled={type === "first" ? props.selected === 1 ? true : false : props.selected === props.count ? true : false}
                  size="small"
                  type="button"
                  style={{
                    fontWeight: selected ? "bold" : undefined,
                  }}
                  variant="outlined"
                  classes={{
                    root: selected
                      ? classes.paginationSelected
                      : classes.paginationRoot,
                  }}
                  sx={{ margin: "0 5px" }}
                >
                  {type === "first" ? <>&lt;&lt;</> : <>&gt;&gt;</>}
                </Button>
              );
            } else {
              children = (
                <Button
                  {...item}
                  disabled={type === "previous" ? props.selected === 1 ? true : false : props.selected === props.count ? true : false}
                  size="small"
                  type="button"
                  variant="outlined"
                  sx={{ textTransform: "none" }}
                  classes={{
                    root: classes.paginationRoot,
                  }}
                >
                  {type === "previous"
                    ? t("label.previous")
                    : type === "next"
                      ? t("label.next")
                      : ""}
                </Button>
              );
            }
            return <li key={index}>{children}</li>;
          })
        ) : (
          <></>
        )}
      </List>
    </nav>
  );
}

const useStyles = makeStyles({
  plusSvg: {
    "& > path": {
      fill: "#fff",
      stroke: "#fff",
    },
  },
  draggingListItem: {
    background: "rgb(235,235,235)",
  },
  toggleBtnRoot: {
    background: `transparent !important`,
    color: "#6B6B6B !important",
    width: "320px",
    border: "none !important",
  },
  toggleBtnSelected: {
    background: `${cssVariables.color.primary}!important`,
    color: "#fff!important",
  },
  flexPaper: {
    flex: 1,
    minWidth: 350,
    "box-shadow": "0px 2px 10px rgba(0, 0, 0, 0.1) !important",
    "border-radius": "10px !important        ",
  },
  tab: {
    fontSize: "16px !important",
    fontWeight: "300 !important",
    // color: "#A3A3A3 !important",
    "&.Mui-selected": {
      color: cssVariables.color.primary + " !important",
    },
  },
  root: {
    display: "flex",
    width: "100%",
    flexWrap: "wrap",
  },
});
const styles = {
  link: {
    color: "#5DA196",
    cursor: "pointer",
  },
  end: {
    color: "#A3A3A3",
  },
};

const Announcement = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [list, setList] = React.useState([]);
  const [filteredList, setFilteredList] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [perPage, setPerPage] = React.useState(10);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [totalPage, setTotalPage] = React.useState(1);
  const [checkResired, setCheckRsquired] = React.useState({});
  const [reSet, setreSet] = React.useState(false);
  const anchorRef = React.useRef(null);
  const [active, setActive] = React.useState(true);
  const [notActive, setNotActive] = React.useState(false);
  const [itemDel, setItemDel] = React.useState("");

  const doGetAnnouncementList = () => {
    dispatch(getAnnouncementList()).then((resp) => {
      if (resp?.data?.news) {
        let modifiedList = resp.data.news.map((item, index) => {
          return {
            ...item,
            number: index + 1
          }
        })
        setList(modifiedList)
        // setFilteredList(modifiedList)
      }
    });
  };
  useEffect(() => {
    doGetAnnouncementList()
  }, []);

  const displayTagsData = (tags) => {
    let tmpTags = tags.join(",")
    return tmpTags
  }
  function handleListKeyDown(event) {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  }
  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };
  const filterActiveSearch = (data) => {
    let result = [];
    let dataFilter = JSON.parse(JSON.stringify(data));
    if (notActive === false && active === false) {
      setFilteredList(result);
    } else if (notActive === true && active === true) {
      setFilteredList(data);
    } else {
      if (notActive === true) {
        let resultData = dataFilter.filter((rs) => rs?.status === "inactive");
        result = resultData;
        setFilteredList(result);
      } else if (active === true) {
        let resultData = dataFilter.filter((rs) => rs?.status === "active");
        result = resultData;
        setFilteredList(result);
      }
    }
  };

  const EditNotActive = (data) => {
    if (data === "notActive") {
      setNotActive(!notActive);
    } else if (data === "active") {
      setActive(!active);
    } else if (data === "selectAll") {
      if (notActive === true && active === true) {
        setNotActive(false);
        setActive(false);
      } else if (notActive === false && active === false) {
        setNotActive(true);
        setActive(true);
      } else if (notActive === true || active === true) {
        setNotActive(true);
        setActive(true);
      }
    }
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };
  const handlesearch = (event) => {
    setSearch(event.target.value);
  };

  const currentData = useMemo(() => {
    const firstPageIndex = (currentPage - 1) * perPage;
    const lastPageIndex = firstPageIndex + perPage;
    return filteredList.slice(firstPageIndex, lastPageIndex);
  }, [filteredList, currentPage, perPage]);

  const handleTotalPage = () => {
    const List = list;
    const totalPage =
      Math.ceil(List.length / perPage) === 0
        ? 1
        : Math.ceil(List.length / perPage);
    setTotalPage(totalPage);
  };
  useEffect(() => {
    handleTotalPage();
  }, [list, perPage]);

  useEffect(() => {
    let update = checkResired;
    if (Object.keys(checkResired)[0] !== undefined) {
      if (update[Object.keys(checkResired)[0]] === false) {
        currentData.sort(function (b, a) {
          if (
            a[Object.keys(checkResired)[0]] > b[Object.keys(checkResired)[0]]
          ) {
            return -1;
          }
          if (
            b[Object.keys(checkResired)[0]] > a[Object.keys(checkResired)[0]]
          ) {
            return 1;
          }
          return 0;
        });
        setreSet(!reSet);
      } else if (update[Object.keys(checkResired)[0]] === true) {
        currentData.sort(function (a, b) {
          if (
            a[Object.keys(checkResired)[0]] > b[Object.keys(checkResired)[0]]
          ) {
            return -1;
          }
          if (
            b[Object.keys(checkResired)[0]] > a[Object.keys(checkResired)[0]]
          ) {
            return 1;
          }
          return 0;
        });
        setreSet(!reSet);
      }
    }
  }, [currentData]);

  useEffect(() => {
    doSearch()
  }, [list, search, notActive, active])

  const doSearch = (data) => {
    let result = [];
    let dataSearch = JSON.parse(
      JSON.stringify(
        data !== undefined && data !== null ? data : list
      )
    );
    if (
      search !== "" &&
      dataSearch !== undefined &&
      dataSearch.length > 0
    ) {
      let resultData = dataSearch.filter((rs) => {
        return Object.keys(rs).some((key) => {
          if (
            key === "title" ||
            key === "excerpt" ||
            key === "created_by" ||
            key === "updated_by"
          ) {
            return (
              rs[key]
                .toString()
                .toLowerCase()
                .indexOf(search.toString().toLowerCase()) !== -1
            );
          }
          if (key === "tags") {
            if (rs[key] !== undefined && rs[key] !== null && rs[key] !== "") {
              return Object.keys(rs[key]).some((keys) => {
                return (
                  rs[key][keys]
                    .toString()
                    .toLowerCase()
                    .indexOf(search.toString().toLowerCase()) !== -1
                );
              });
            }
          }
        });
      });
      result = resultData;
    } else {
      result = dataSearch;
    }
    filterActiveSearch(result);
  };

  const clickLink = (data) => {
    navigate(data, { replace: false });
  };

  const handlePerPageChange = (event) => {
    setPerPage(event.target.value);
  };
  const handleSetCurrentPage = (value) => {
    const getSelectedPage = value.filter(
      (item) => item.type === "page" && item.selected
    );
    setCurrentPage(getSelectedPage.length > 0 ? getSelectedPage[0].page : 1);
  };
  const toTimestamp = (strDate) => {
    if (strDate !== "" && strDate !== null && strDate !== undefined) {
      const dt = Date.parse(strDate);
      return dt / 1000;
    } else {
      return 0;
    }
  };
  let sortData = (key, check1) => {
    let update = checkResired;
    if (check1 === false && update[key] !== check1) {
      if (key === "created_by" || key === "updated_by") {
        currentData.sort(function (a, b) {
          if (Util.formatData(a[key]?.fullname, "") > Util.formatData(b[key]?.fullname, "")) {
            return -1;
          }
          if (Util.formatData(b[key]?.fullname, "") > Util.formatData(a[key]?.fullname, "")) {
            return 1;
          }
          return 0;
        });
      } else {
        currentData.sort(function (b, a) {
          if (a[key] > b[key]) {
            return -1;
          }
          if (b[key] > a[key]) {
            return 1;
          }
          return 0;
        });
      }
      update = { [key]: false };
      setCheckRsquired(update);
      setreSet(!reSet);
    } else if (check1 === true && update[key] !== check1) {
      if (key === "created_by" || key === "updated_by") {
        currentData.sort(function (a, b) {
          if (Util.formatData(a[key]?.fullname, "") > Util.formatData(b[key]?.fullname, "")) {
            return -1;
          }
          if (Util.formatData(b[key]?.fullname, "") > Util.formatData(a[key]?.fullname, "")) {
            return 1;
          }
          return 0;
        });
      } else {
        currentData.sort(function (a, b) {
          if (a[key] > b[key]) {
            return -1;
          }
          if (b[key] > a[key]) {
            return 1;
          }
          return 0;
        });
      }
      update = { [key]: true };
      setCheckRsquired(update);
      setreSet(!reSet);
    }
  };
  const onScroll = (evt) => {
    if (evt !== undefined && evt !== null) {
      if (open) {
        setOpen(false);
      }
    }
  };
  const BootstrapTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
      color: "#343A40",
    },
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: "#343A40",
      color: "#FFFFFF",
      padding: "11px 15px",
    },
  }));

  const [isConfirmDeleteDialogOpen, openConfirmDeleteDialog] = React.useState(false);
  const handleConfirmDeleteDialog = () => {
    openConfirmDeleteDialog(false)
    dispatch(deleteAnnouncement(itemDel)).then(() => {
      doGetAnnouncementList()
    })
  };
  const handleDeleteClick = React.useCallback((e, data) => {
    e.stopPropagation();
    e.preventDefault();
    setItemDel(data)
    openConfirmDeleteDialog(true)
  }, []);

  return (
    <Box
      onScroll={onScroll}
      style={{ "max-height": "100%", "overflow-y": "auto" }}
    >
      <Helmet>
        <title>Admin - Announcement | PMS </title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: "background.default",
          minHeight: "100%",
          py: 3,
        }}
      >
        <Container maxWidth="xl">
          <Grid container pt={2} pb={2}>
            <Typography fontSize={16} lineHeight={1} color="#68717A">
              <span style={styles.end}>{t("admin.announcement.title")}</span>
            </Typography>
          </Grid>
          <Grid container item>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
              pt="7px"
            >
              <Typography
                color="#404349"
                fontSize={36}
                fontWeight="600"
              >
                {t("admin.announcement.title")}
              </Typography>
            </Box>
          </Grid>
          <Grid container spacing={2}>
            <Grid container item>
              <Box sx={{ width: "100%" }}>
                <Box
                  pb="30px"
                  pt="30px"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <TextField
                    id="input-with-icon-textfield"
                    fullWidth
                    placeholder={t("admin.announcement.searchPlaceholder")}
                    size="small"
                    sx={{
                      background: "#fff",
                      width: "400px",
                      input: { padding: "11px 14px" },
                    }}
                    className="searchTextField-h40"
                    value={search}
                    onChange={handlesearch}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search style={{ color: "#C4C4C4" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    size="small"
                    aria-controls="simple-menu"
                    aria-haspopup="true"
                    onClick={() => clickLink('/admin/announcement/create')}
                    color="primary"
                    variant="contained"
                    sx={{ width: "129px", height: "45px", p: "0", textTransform: "none", "margin-left": "30px" }}
                  >
                    <PlusIcon className={classes.plusSvg} />
                    <Typography ml={1} fontWeight="500" fontSize={18} lineHeight="26.91px">
                      {t("label.create")}
                    </Typography>
                  </Button>
                </Box>
              </Box>
              <div className={classes.root}>
                <Paper className={classes.flexPaper}>
                  {/* TABLE HEADER */}
                  <Box px="40px">
                    <Box
                      px="25px"
                      py="19px"
                      sx={{ display: "flex", "flex-direction": "row" }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          "flex-direction": "row",
                          width: "calc(7% - 12px)",
                        }}
                        pr="15px"
                      />
                      <Box
                        sx={{
                          display: "flex",
                          "flex-direction": "row",
                          width: "calc(6% - 12px)",
                        }}
                        pr="15px"
                      >
                        <Typography
                          fontSize={16}
                          lineHeight="23.96px"
                          color="#A0A0A0"
                          fontWeight="300px"
                          sx={{
                            whiteSpace: "nowrap",
                            width: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            "max-width": "max-content",
                            "margin-right": "10px",
                          }}
                        >
                          {t("admin.announcement.no")}
                        </Typography>
                        <Typography
                          component="span"
                          style={{
                            "flex-direction": "column",
                            "justify-content": "center",
                            display: "flex",
                          }}
                        >
                          <ChevronExpandUp
                            style={{ cursor: "pointer" }}
                            onClick={(e) => sortData("number", true)}
                          />
                          <ChevronExpandDown
                            style={{ cursor: "pointer" }}
                            onClick={(e) => sortData("number", false)}
                          />
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          "flex-direction": "row",
                          width: "calc(10% - 12px)",
                        }}
                        pr="15px"
                      >
                        <Typography
                          fontSize={16}
                          lineHeight="23.96px"
                          color="#A0A0A0"
                          fontWeight="300px"
                          sx={{
                            whiteSpace: "nowrap",
                            width: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            "max-width": "max-content",
                            "margin-right": "10px",
                          }}
                        >
                          {t("admin.announcement.banner")}
                        </Typography>
                        <Typography
                          component="span"
                          style={{
                            "flex-direction": "column",
                            "justify-content": "center",
                            display: "flex",
                          }}
                        >
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          "flex-direction": "row",
                          width: "calc(25% - 12px)",
                        }}
                        pr="15px"
                      >
                        <Typography
                          fontSize={16}
                          lineHeight="23.96px"
                          color="#A0A0A0"
                          fontWeight="300px"
                          sx={{
                            whiteSpace: "nowrap",
                            width: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            "max-width": "max-content",
                            "margin-right": "10px",
                          }}
                        >
                          {t("admin.announcement.topic")}
                        </Typography>
                        <Typography
                          component="span"
                          style={{
                            "flex-direction": "column",
                            "justify-content": "center",
                            display: "flex",
                          }}
                        >
                          <ChevronExpandUp
                            style={{ cursor: "pointer" }}
                            onClick={(e) => sortData("title", true)}
                          />
                          <ChevronExpandDown
                            style={{ cursor: "pointer" }}
                            onClick={(e) => sortData("title", false)}
                          />
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          "flex-direction": "row",
                          width: "calc(20% - 12px)",
                        }}
                        pr="15px"
                      >
                        <Typography
                          fontSize={16}
                          lineHeight="23.96px"
                          color="#A0A0A0"
                          fontWeight="300px"
                          sx={{
                            whiteSpace: "nowrap",
                            width: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            "max-width": "max-content",
                            "margin-right": "10px",
                          }}
                        >
                          {t("admin.announcement.announcementDate")}
                        </Typography>
                        <Typography
                          component="span"
                          style={{
                            "flex-direction": "column",
                            "justify-content": "center",
                            display: "flex",
                          }}
                        >
                          <ChevronExpandUp
                            style={{ cursor: "pointer" }}
                            onClick={(e) => sortData("start_at", true)}
                          />
                          <ChevronExpandDown
                            style={{ cursor: "pointer" }}
                            onClick={(e) => sortData("start_at", false)}
                          />
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          "flex-direction": "row",
                          width: "calc(12% - 12px)",
                        }}
                        pr="15px"
                      >
                        <Typography
                          fontSize={16}
                          lineHeight="23.96px"
                          color="#A0A0A0"
                          fontWeight="300px"
                          sx={{
                            whiteSpace: "nowrap",
                            width: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            "max-width": "max-content",
                            "margin-right": "10px",
                          }}
                        >
                          {t("label.formStatus")}
                        </Typography>
                        {/* status stack */}
                        <Stack direction="row" spacing={2}>
                          <div>
                            <FilterStatus
                              ref={anchorRef}
                              id="composition-button"
                              style={{ cursor: "pointer" }}
                              onClick={handleToggle}
                            />
                            <Popper
                              open={open}
                              anchorEl={anchorRef.current}
                              role={undefined}
                              placement="bottom-start"
                              transition
                              disablePortal
                              style={{ "z-index": "1" }}
                            >
                              {({ TransitionProps, placement }) => (
                                <Grow
                                  {...TransitionProps}
                                  style={{
                                    transformOrigin:
                                      placement === "bottom-start"
                                        ? "left top"
                                        : "left bottom",
                                  }}
                                >
                                  <Paper
                                    sx={{ "margin-top": "5px !important" }}
                                  >
                                    <ClickAwayListener
                                      onClickAway={handleClose}
                                    >
                                      <MenuList
                                        autoFocusItem={open}
                                        id="composition-menu"
                                        aria-labelledby="composition-button"
                                        onKeyDown={handleListKeyDown}
                                      >
                                        <Box px="16px" py="6px">
                                          <FormGroup>
                                            <FormControlLabel
                                              control={
                                                <Checkbox
                                                  indeterminate={
                                                    notActive !== active
                                                  }
                                                  onChange={(e) =>
                                                    EditNotActive("selectAll")
                                                  }
                                                  checked={
                                                    notActive === true &&
                                                      active === true
                                                      ? true
                                                      : false
                                                  }
                                                />
                                              }
                                              label={t("competency.selectAll")}
                                            />
                                            <FormControlLabel
                                              control={
                                                <Checkbox
                                                  onChange={(e) =>
                                                    EditNotActive("notActive")
                                                  }
                                                  checked={notActive}
                                                />
                                              }
                                              label={t("admin.announcement.inactive")}
                                            />
                                            <FormControlLabel
                                              control={
                                                <Checkbox
                                                  onChange={(e) =>
                                                    EditNotActive("active")
                                                  }
                                                  checked={active}
                                                />
                                              }
                                              label={t("admin.announcement.active")}
                                            />
                                          </FormGroup>
                                        </Box>
                                      </MenuList>
                                    </ClickAwayListener>
                                  </Paper>
                                </Grow>
                              )}
                            </Popper>
                          </div>
                        </Stack>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          "flex-direction": "row",
                          width: "calc(12% - 12px)",
                        }}
                        pr="15px"
                      >
                        <Typography
                          fontSize={16}
                          lineHeight="23.96px"
                          color="#A0A0A0"
                          fontWeight="300px"
                          sx={{
                            whiteSpace: "nowrap",
                            width: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            "max-width": "max-content",
                            "margin-right": "10px",
                          }}
                        >
                          {t("admin.announcement.createdBy")}
                        </Typography>
                        <Typography
                          component="span"
                          style={{
                            "flex-direction": "column",
                            "justify-content": "center",
                            display: "flex",
                          }}
                        >
                          <ChevronExpandUp
                            style={{ cursor: "pointer" }}
                            onClick={(e) => sortData("created_by", true)}
                          />
                          <ChevronExpandDown
                            style={{ cursor: "pointer" }}
                            onClick={(e) => sortData("created_by", false)}
                          />
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          "flex-direction": "row",
                          width: "calc(12% - 12px)",
                        }}
                        pr="15px"
                      >
                        <Typography
                          fontSize={16}
                          lineHeight="23.96px"
                          color="#A0A0A0"
                          fontWeight="300px"
                          sx={{
                            whiteSpace: "nowrap",
                            width: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            "max-width": "max-content",
                            "margin-right": "10px",
                          }}
                        >
                          {t("admin.announcement.updatedBy")}
                        </Typography>
                        <Typography
                          component="span"
                          style={{
                            "flex-direction": "column",
                            "justify-content": "center",
                            display: "flex",
                          }}
                        >
                          <ChevronExpandUp
                            style={{ cursor: "pointer" }}
                            onClick={(e) => sortData("updated_by", true)}
                          />
                          <ChevronExpandDown
                            style={{ cursor: "pointer" }}
                            onClick={(e) => sortData("updated_by", false)}
                          />
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          "flex-direction": "row",
                          width: "60px",
                        }}
                      >
                        <Typography
                          fontSize={16}
                          lineHeight="23.96px"
                          color="#A0A0A0"
                          fontWeight="300px"
                          sx={{
                            whiteSpace: "nowrap",
                            width: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {t("admin.announcement.action")}
                        </Typography>
                      </Box>
                    </Box>
                    <Divider width="100%" />
                  </Box>
                  {currentData !== undefined &&
                    currentData !== null &&
                    currentData.length > 0 ? (
                    currentData.map((row, index) => (
                      <Box px="40px">
                        <Box
                          sx={{
                            width: "100%",
                            position: "relative",
                            "&:before": {
                              content: '""',
                              position: "absolute",
                              height:
                                row?.status === "inactive" ? "100%" : "0px",
                              width:
                                row?.status === "inactive" ? "100%" : "0px",
                              top: "0",
                              background: "#FAFAFA",
                              opacity: "0.7",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              height: "96px",
                              background: "#FFF",
                              width: "100%",
                              display: "flex",
                              alignItems: "center",
                            }}
                            px="25px"
                          >
                            <Box sx={{ width: "calc(7% - 12px)" }} pr="15px">
                              <Typography
                                fontSize={16}
                                lineHeight="23.96px"
                                fontWeight="300px"
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: "100%",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                <BootstrapTooltip title={`Recommended`} placement="top">
                                  {!row?.show_home ?
                                    <StarEmpty />
                                    :
                                    <StarFill />
                                  }
                                </BootstrapTooltip>
                              </Typography>
                            </Box>
                            <Box sx={{ width: "calc(6% - 12px)" }} pr="15px">
                              <Typography
                                fontSize={16}
                                lineHeight="23.96px"
                                fontWeight="300px"
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: "100%",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {row?.number}
                              </Typography>
                            </Box>
                            <Box sx={{ width: "calc(10% - 12px)" }} pr="15px">
                              <img src={row?.thumbnail ? row.thumbnail : defaultBanner} alt="Default Banner" style={{ width: "115px", height: "81px", borderRadius: "4px" }} />
                            </Box>
                            <Box
                              sx={{
                                width: "calc(25% - 12px)",
                                display: "flex",
                                justifyContent: "flex-start",
                                alignItems: "center",
                              }}
                              pr="15px"
                            >
                              <Box ml={0}>
                                <Typography
                                  fontWeight="400"
                                  fontSize={16}
                                  color="#404349"
                                  sx={{
                                    width: "320px",
                                    maxHeight: "120px",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    "-webkit-line-clamp": "2",
                                    "-webkit-box-orient": "vertical",
                                    whiteSpace: "normal",
                                    display: "-webkit-box"
                                  }}
                                >
                                  {row?.title}
                                </Typography>
                                <Typography
                                  fontWeight="400"
                                  fontSize={14}
                                  color="#A3A3A3"
                                  sx={{
                                    width: "320px",
                                    maxHeight: "120px",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    "-webkit-line-clamp": "2",
                                    "-webkit-box-orient": "vertical",
                                    whiteSpace: "normal",
                                    display: "-webkit-box"
                                  }}
                                >
                                  {t("admin.announcement.tags")}: {row?.tags?.length > 0 ? displayTagsData(row.tags) : "-"}
                                </Typography>
                              </Box>
                            </Box>
                            <Box sx={{ width: "calc(20% - 12px)" }} pr="15px">
                              <Typography
                                fontWeight="400"
                                fontSize={16}
                                lineHeight="23.92px"
                                color="#404349"
                                sx={{
                                  width: "100%",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "pre-wrap"
                                }}
                              >
                                {Util.dateDayFormat(row?.start_at)} ({Util.dateTimeFormat(row?.start_at)}) {row?.end_at !== "0001-01-01T00:00:00Z" && "-\n"}
                                {row?.end_at !== "0001-01-01T00:00:00Z" && Util.dateDayFormat(row?.end_at)} {row?.end_at !== "0001-01-01T00:00:00Z" && `(${Util.dateTimeFormat(row?.end_at)})`}
                              </Typography>
                            </Box>
                            <Box sx={{ width: "calc(12% - 12px)" }} pr="15px">
                              <Typography
                                fontWeight="400"
                                fontSize={16}
                                lineHeight="23.92px"
                                sx={{
                                  color: "#5DA196",
                                  whiteSpace: "nowrap",
                                  width: "100%",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {row?.status === "active"
                                  ? "ACTIVE"
                                  : "INACTIVE"}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                "flex-direction": "column",
                                width: "calc(12% - 12px)",
                              }}
                              pr="15px"
                            >
                              <Typography
                                fontWeight="400"
                                fontSize={16}
                                lineHeight="24px"
                                color="#404349"
                                sx={{
                                  width: "100%"
                                }}
                              >
                                {`${row?.created_by}`}
                              </Typography>
                              <Typography
                                fontWeight="400"
                                fontSize={14}
                                color="#A3A3A3"
                                sx={{
                                  width: "100%"
                                }}
                              >
                                {row?.created_at !== "" && Util.dateDayFormat(row?.created_at)} {row?.created_at !== "" && `(${Util.dateTimeFormat(row?.created_at)})`}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                "flex-direction": "column",
                                width: "calc(12% - 12px)",
                              }}
                              pr="15px"
                            >
                              <Typography
                                fontWeight="400"
                                fontSize={16}
                                lineHeight="24px"
                                sx={{
                                  color: "#404349",
                                  whiteSpace: "nowrap",
                                  width: "100%",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {`${row?.updated_by !== "" ? row?.updated_by : "-"}\n`}
                              </Typography>
                              <Typography
                                fontWeight="400"
                                fontSize={14}
                                color="#A3A3A3"
                                sx={{
                                  width: "100%"
                                }}
                              >
                                {row?.updated_at !== "" && Util.dateDayFormat(row?.updated_at)} {row?.updated_at !== "" && `(${Util.dateTimeFormat(row?.updated_at)})`}
                              </Typography>
                            </Box>
                            <Box sx={{ display: "flex", "flex-direction": "row", width: "60px", "justify-content": "flex-end" }}>
                              <Box sx={{ display: "flex", "flex-direction": "row", width: "100px", "z-index": "1" }}>
                                <Box pr="20px">
                                  <BootstrapTooltip title={`Edit`} placement="top">
                                    <EditIcon style={{ cursor: "pointer" }} onClick={() => { clickLink("/admin/announcement/edit/" + row?.serial) }} />
                                  </BootstrapTooltip>
                                </Box>
                                {row?.status === "active" ?
                                  <Box>
                                    <DeleteIcon style={{ opacity: "0.2", cursor: "no-drop", pointerEvents: "none" }} />
                                  </Box>
                                  :
                                  <Box>
                                    <BootstrapTooltip title={`Delete`} placement="top">
                                      <DeleteIcon style={{ cursor: "pointer" }} onClick={(e) => { handleDeleteClick(e, row?.serial) }} />
                                    </BootstrapTooltip>
                                  </Box>
                                }
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                        <Divider width="100%" />
                      </Box>
                    ))
                  ) : (
                    <Box
                      sx={{
                        "padding-top": "50px",
                        "text-align": "center",
                        "padding-bottom": "500px",
                      }}
                    >
                      <Nodata />
                      <Typography
                        color="#A0A0A0"
                        fontWeight="500"
                        fontSize={20}
                        lineHeight="30px"
                      >
                        {t("label.noData")}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </div>
            </Grid>
          </Grid>
          <Grid container mt={1} spacing={2}>
            <Grid item xs sm md display="flex" alignItems="center">
              <Box mr={1}>
                <Typography variant="s14_fw400">
                  {t("label.showMaxItems")}
                </Typography>
              </Box>
              <Select
                id="pagination"
                variant="outlined"
                defaultValue={10}
                value={perPage}
                onChange={handlePerPageChange}
                displayEmpty
                size="small"
                className="white-bg"
              >
                <MenuItem value={10}>
                  <Typography style={{ fontWeight: "300" }}>10</Typography>
                </MenuItem>
                <MenuItem value={20}>
                  <Typography style={{ fontWeight: "300" }}>20</Typography>
                </MenuItem>
                <MenuItem value={30}>
                  <Typography style={{ fontWeight: "300" }}>30</Typography>
                </MenuItem>
              </Select>
            </Grid>
            <Grid item display="flex" alignItems="center" ml="auto">
              <Typography variant="s14_fw400">
                {t("label.page")} {currentPage} {t("label.of")} {totalPage}
              </Typography>
            </Grid>
            <Grid item ml="auto" display="flex" alignItems="center">
              <CustomPagination
                setCurrentPage={handleSetCurrentPage}
                selected={currentPage}
                count={totalPage}
              ></CustomPagination>
            </Grid>
          </Grid>
          <ConfirmDialog open={isConfirmDeleteDialogOpen} trash={true} doConfirm={handleConfirmDeleteDialog} setValue={openConfirmDeleteDialog} title={t("admin.announcement.dialog.deleteTitle")} text={t("admin.announcement.dialog.deleteText")} />
        </Container>
      </Box>
    </Box>
  );
};
export default Announcement;
